import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { Dataset, formatConst, LinkedService, Metamodel, Owned, storeConst } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { SqlDBSettings } from 'app/model/dataflow/dataset-settings/sql-db-settings';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable, ISqlBasedMapperEntity, SchemaLoadStatus } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { AzureSqlTableDataset } from 'app/model/dataset/azure-sql-table.dataset';
import { getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { fetchEntitiesForSQLBasedEntity, supportedSqlDatasetFormatToDatasetSettingMap } from './sqlentity-component-utils';
import { SqlMISettings } from 'app/model/dataflow/dataset-settings/sql-mi-settings';
import { OnPremiseSqlServerSettings } from 'app/model/dataflow/dataset-settings/on-premise-sql-server-settings';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';
import { LoggerService } from 'app/logger/logger.service';


export class SqlServerDatasetTypeHandler extends DatasetTypeHandler {

    constructor(
        public isSource: Boolean
     ){
         super();
     }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        if(supportedSqlDatasetFormatToDatasetSettingMap.has(selectedDatasetFormat)){
            outputSource.datasetSettings = Owned.create(Metamodel.modelByName(supportedSqlDatasetFormatToDatasetSettingMap.get(selectedDatasetFormat)), outputSource);

            if(selectedDatasetFormat === DatasetFormatType.OnPremiseSqlServer){
                const incomingSourceDatasetsSettings = inputSource.datasetSettings as OnPremiseSqlServerSettings;
                const outputSourceDatasetsSettings = outputSource.datasetSettings as OnPremiseSqlServerSettings;
                outputSource.datasetSettings.initialize('');
                outputSourceDatasetsSettings.schemaName.expression = !incomingSourceDatasetsSettings.schemaName ? undefined : incomingSourceDatasetsSettings.schemaName.expression;
                outputSourceDatasetsSettings.tableName.expression = !incomingSourceDatasetsSettings.tableName ? undefined : incomingSourceDatasetsSettings.tableName.expression;
                outputSource.wildcardPaths = inputSource.wildcardPaths;
                outputSource.datasetSettings = outputSourceDatasetsSettings;
            } else if(selectedDatasetFormat === DatasetFormatType.SqlDB){
                const incomingSourceDatasetsSettings = inputSource.datasetSettings as SqlDBSettings;
                const outputSourceDatasetsSettings = outputSource.datasetSettings as SqlDBSettings;
                outputSource.datasetSettings.initialize('');
                outputSourceDatasetsSettings.schemaName.expression = !incomingSourceDatasetsSettings.schemaName ? undefined : incomingSourceDatasetsSettings.schemaName.expression;
                outputSourceDatasetsSettings.tableName.expression = !incomingSourceDatasetsSettings.tableName ? undefined : incomingSourceDatasetsSettings.tableName.expression;
                outputSource.wildcardPaths = inputSource.wildcardPaths;
                outputSource.datasetSettings = outputSourceDatasetsSettings;
            } else if(selectedDatasetFormat === DatasetFormatType.SqlMI){
                const incomingSourceDatasetsSettings = inputSource.datasetSettings as SqlMISettings;
                const outputSourceDatasetsSettings = outputSource.datasetSettings as SqlMISettings;
                outputSource.datasetSettings.initialize('');
                outputSourceDatasetsSettings.schemaName.expression = !incomingSourceDatasetsSettings.schemaName ? undefined : incomingSourceDatasetsSettings.schemaName.expression;
                outputSourceDatasetsSettings.tableName.expression = !incomingSourceDatasetsSettings.tableName ? undefined : incomingSourceDatasetsSettings.tableName.expression;
                outputSource.wildcardPaths = inputSource.wildcardPaths;
                outputSource.datasetSettings = outputSourceDatasetsSettings;
            }
        }
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[], isTargetPanel: boolean): [boolean, string] {
        let disableButton = true;
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as ISqlBasedMapperEntity[];
            for (const row of rows) {
                if (!isTargetPanel && row.isNativeCDC === false && row.watermarkSelected === undefined) {
                    disableButton = true;
                    break;
                }
            }
            return [disableButton, ''];
        }
        return [disableButton, ''];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        const rows = selectedRows as ISqlBasedMapperEntity[];
        rows.forEach((elem) => {
            const srcName = elem.entityName.replace(/\[|\]/g, '');
            const tableName: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, selectedSourceLinkedService);
            tableName.expression = srcName.includes('.') ? srcName.split('.')[1] : srcName;
            const schemaName: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, selectedSourceLinkedService);
            schemaName.expression = srcName.includes('.') ? srcName.split('.')[0] : srcName;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dslProps = new Map<string,any>([
                [ DslConnectorConstants.schemaName, DataFlowExpressionUtils.escapeSingleQuoteInDslString(schemaName.expression)],
                [ DslConnectorConstants.tableName, DataFlowExpressionUtils.escapeSingleQuoteInDslString(tableName.expression)],
            ]);
            if (elem.isNativeCDC === true) {
                dslProps.set('enableNativeCdc',  true);
                dslProps.set('netChanges', true);
            }
            else if (!!elem.watermarkSelected) {
                dslProps.set('enableCdc', true);
                dslProps.set(DslConnectorConstants.waterMarkColumn, elem.watermarkSelected);
            }

            tables.push({
                // need a valid mapping dataflow source name to pass dataflow validations
                name: getValidTransformationName(this.isSource, srcName),
                sourceDataset: elem.dataset,
                sourceLinkedService: selectedSourceLinkedService,
                isInlineDataset: true,
                // will initialize datasetSettings post processing Source|Target Sql entity.
                datasetSettings: undefined,
                wildcardPaths: [],
                dslConnectorProperties: dslProps,
                datasetType: selectedDatasetFormat,
                schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
                tableName: srcName
            });
        });


        return tables;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const schemaName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaName);
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);

        // Create and populate SqlDBSettings
        if(table.datasetType === DatasetFormatType.OnPremiseSqlServer){
            const sqlServerSettings =  Owned.create<OnPremiseSqlServerSettings>(OnPremiseSqlServerSettings.MODEL, table as DataFlowSource);
            sqlServerSettings.initialize(undefined);
            sqlServerSettings.schemaName = createDataflowExpression(schemaName) as DataFlowExpression;
            sqlServerSettings.tableName = createDataflowExpression(tableName) as DataFlowExpression;
            return sqlServerSettings;
        } else if(table.datasetType === DatasetFormatType.SqlDB){
            const sqlServerSettings =  Owned.create<SqlDBSettings>(SqlDBSettings.MODEL, table as DataFlowSource);
            sqlServerSettings.initialize(undefined);
            sqlServerSettings.schemaName = createDataflowExpression(schemaName) as DataFlowExpression;
            sqlServerSettings.tableName = createDataflowExpression(tableName) as DataFlowExpression;
            return sqlServerSettings;
        } else if(table.datasetType === DatasetFormatType.SqlMI){
            const sqlServerSettings =  Owned.create<SqlMISettings>(SqlMISettings.MODEL, table as DataFlowSource);
            sqlServerSettings.initialize(undefined);
            sqlServerSettings.schemaName = createDataflowExpression(schemaName) as DataFlowExpression;
            sqlServerSettings.tableName = createDataflowExpression(tableName) as DataFlowExpression;
            return sqlServerSettings;
        }
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const schemaName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaName);
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);
        const dataset = new AzureSqlTableDataset('SqlServer');
        dataset.linkedService = linkedService;
        dataset.table = tableName;
        dataset.schema = schemaName;
        return dataset;
    }

    public updateMapperTablePropertiesOnEdit(existingMapperTable: IMapperTable, updatedMapperTable: IMapperTable): void {
        if (this.isSource && updatedMapperTable.dslConnectorProperties.has(DslConnectorConstants.waterMarkColumn)) {
            const watermarkColumnName = updatedMapperTable.dslConnectorProperties.get(DslConnectorConstants.waterMarkColumn);
            existingMapperTable.dslConnectorProperties.set(DslConnectorConstants.waterMarkColumn, watermarkColumnName);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async fetchEntities(loggerService: LoggerService, queryApiFactoryService: QueryApiFactoryService, linkedService: LinkedService): Promise<any[]> {
        if(!this.isSource) {
            return await queryApiFactoryService.getWizardQueryApi().getNavTableAsync(linkedService);
        }
        return fetchEntitiesForSQLBasedEntity(queryApiFactoryService, linkedService, 'AzureSqlTable', loggerService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);

        const commonDslConnectorProperties = mapperConnectionInfo.connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.format, formatConst.SQL);
        commonDslConnectorProperties.set(DslConnectorConstants.store, storeConst.SQL);

        if (this.isSource) {
            commonDslConnectorProperties.set('skipInitialLoad', true);
        } else if(selectedDatasetFormat === DatasetFormatType.SqlDB || selectedDatasetFormat === DatasetFormatType.SqlMI) {
            commonDslConnectorProperties.set(DslConnectorConstants.deletable, false);
            commonDslConnectorProperties.set(DslConnectorConstants.insertable, true);
            commonDslConnectorProperties.set(DslConnectorConstants.updateable, false);
            commonDslConnectorProperties.set(DslConnectorConstants.upsertable, false);
            commonDslConnectorProperties.set('skipDuplicateMapInputs', true);
            commonDslConnectorProperties.set('skipDuplicateMapOutputs', true);
        }
    }
}
